import React, { useState } from 'react';
import { Card, Row, Col, Button, Collapse } from 'react-bootstrap';
import { ChevronDown, ChevronUp, User, Phone, Mail, Calendar, Building2, MapPin, Globe, CreditCard, Hash, Landmark } from 'lucide-react';

const DoctorsProfile = ({ data }) => {
     const [showConfidential, setShowConfidential] = useState(false);

     if (!data) return <div className="text-center py-4">No data available</div>;

     // Helper to safely render qualification data
     const renderQualification = (qual) => {
          if (!qual) return '—';
          if (typeof qual === 'string') return qual;
          if (Array.isArray(qual) && qual.length > 0) {
               const q = qual[0];
               const parts = [];
               if (q.qualification) parts.push(q.qualification);
               if (q.specialization) parts.push(`(${q.specialization})`);
               if (q.collegeName) parts.push(`- ${q.collegeName}`);
               if (q.yearOfPassing && q.yearOfPassing !== '0000-00-00') parts.push(`[${q.yearOfPassing}]`);
               return parts.length > 0 ? parts.join(' ') : '—';
          }
          if (typeof qual === 'object') {
               const parts = [];
               if (qual.qualification) parts.push(qual.qualification);
               if (qual.specialization) parts.push(`(${qual.specialization})`);
               if (qual.collegeName) parts.push(`- ${qual.collegeName}`);
               if (qual.yearOfPassing && qual.yearOfPassing !== '0000-00-00') parts.push(`[${qual.yearOfPassing}]`);
               return parts.length > 0 ? parts.join(' ') : '—';
          }
          return '—';
     };

     const InfoCard = ({ icon: Icon, label, value, color = '#3b82f6' }) => (
          <Card style={{
               borderRadius: 6,
               border: '1px solid #e5e7eb',
               marginBottom: 12,
               boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
               <Card.Body style={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                         <Icon size={14} style={{ color }} />
                         <small style={{ color: '#6b7280', fontSize: 11, fontWeight: 500 }}>{label}</small>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                         {value || '—'}
                    </div>
               </Card.Body>
          </Card>
     );

     return (
          <div style={{ padding: 12, backgroundColor: '#f9fafb', minHeight: '100vh' }}>
               <div style={{ maxWidth: 1200, margin: '0 auto' }}>

                    {/* Header Card */}
                    <Card style={{
                         borderRadius: 6,
                         border: 'none',
                         marginBottom: 12,
                         boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                         <Card.Body style={{ padding: 14 }}>
                              <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1f2937' }}>
                                   {data.employee_name || 'Doctor Name'}
                              </h4>
                              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                   Emp Code: {data.employee_code || '—'}
                              </div>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>
                                   Designation: {data.designation || '—'}
                              </div>
                         </Card.Body>
                    </Card>

                    {/* Info Grid */}
                    <Row>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={Phone} label="Mobile" value={data.mobile} color="#3b82f6" />
                         </Col>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={Mail} label="Email" value={data.email || data.mail} color="#3b82f6" />
                         </Col>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={User} label="Gender" value={data.gender} color="#f59e0b" />
                         </Col>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={Calendar} label="DOB" value={data.dob} color="#f59e0b" />
                         </Col>
                    </Row>

                    <Row>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={Building2} label="Department" value={data.department} color="#8b5cf6" />
                         </Col>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={Building2} label="OU Name" value={data.location_name} color="#8b5cf6" />
                         </Col>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={MapPin} label="Location" value={data.location_name} color="#ec4899" />
                         </Col>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={Globe} label="Region" value={data.cluster} color="#06b6d4" />
                         </Col>
                    </Row>

                    <Row>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={Globe} label="Cluster" value={data.cluster} color="#06b6d4" />
                         </Col>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={Building2} label="City" value={data.city_name} color="#06b6d4" />
                         </Col>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={CreditCard} label="Pay Group" value={data.pay_group} color="#ec4899" />
                         </Col>
                         <Col lg={3} md={6} sm={6} xs={12}>
                              <InfoCard icon={User} label="Position" value={data.position || data.designation} color="#6366f1" />
                         </Col>
                    </Row>

                    {/* Qualification Section */}
                    <Row>
                         <Col lg={6} md={6} xs={12}>
                              <InfoCard icon={User} label="Qualification" value={renderQualification(data.qualification)} color="#10b981" />
                         </Col>
                         <Col lg={3} md={6} xs={12}>
                              <InfoCard icon={Hash} label="Registration" value={data.registration_number} color="#10b981" />
                         </Col>
                         <Col lg={3} md={6} xs={12}>
                              <InfoCard icon={Building2} label="Council Name" value={data.council_name} color="#10b981" />
                         </Col>
                    </Row>

                    {/* Confidential Section */}
                    <Card style={{
                         borderRadius: 6,
                         border: '1px solid #e5e7eb',
                         boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                         marginTop: 4
                    }}>
                         <Card.Body style={{ padding: 12 }}>
                              <Button
                                   variant="outline-secondary"
                                   size="sm"
                                   onClick={() => setShowConfidential(!showConfidential)}
                                   style={{
                                        fontSize: 12,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        border: '1px solid #d1d5db',
                                        padding: '6px 12px',
                                        borderRadius: 6
                                   }}
                              >
                                   {showConfidential ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                   {showConfidential ? 'Hide' : 'Show'} Other Details
                              </Button>

                              <Collapse in={showConfidential}>
                                   <div style={{ marginTop: 12 }}>
                                        <Row>
                                             <Col lg={3} md={6} xs={12}>
                                                  <InfoCard icon={Hash} label="PAN" value={data.pan_number} color="#6366f1" />
                                             </Col>
                                             <Col lg={3} md={6} xs={12}>
                                                  <InfoCard icon={Hash} label="Aadhar" value={data.aadhar_number} color="#6366f1" />
                                             </Col>
                                             <Col lg={3} md={6} xs={12}>
                                                  <InfoCard icon={Landmark} label="Bank Name" value={data.bank_account_name} color="#6366f1" />
                                             </Col>
                                             <Col lg={3} md={6} xs={12}>
                                                  <InfoCard icon={Hash} label="Bank Account" value={data.bank_account_number} color="#6366f1" />
                                             </Col>
                                        </Row>
                                        <Row>
                                             <Col lg={3} md={6} xs={12}>
                                                  <InfoCard icon={CreditCard} label="IFSC" value={data.ifsc_code} color="#6366f1" />
                                             </Col>
                                        </Row>
                                   </div>
                              </Collapse>
                         </Card.Body>
                    </Card>

               </div>
          </div>
     );
};

export default DoctorsProfile;